import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdKeyboardArrowDown } from "react-icons/md";

import OrderViewHeader from "./OrderViewHeader";
import OrderSummary from "./OrderSummary";
import OrderItems from "./OrderItems";
import OrderActivity from "./OrderActivity";
import OrderScans from "./OrderScans";
import {
  readPicks,
  togglePick,
  writePicks,
} from "../../../../../Shipper/Customer/MainPages/Orders/Components/DeliveryRoutes/routeSheet";
import useRoutePicks from "../../../../../Shipper/Customer/MainPages/Orders/Components/DeliveryRoutes/useRoutePicks";
import {
  boltLines,
  fulfilPlan,
  pickingOver,
  pickStateOf,
  togglePicked,
} from "./picks";

const isDesktop = () =>
  typeof window !== "undefined" &&
  Boolean(window.matchMedia?.("(min-width: 1024px)").matches);

export default function OrderView({
  data,
  handleClose,
  downloadPdf,
  // (optional) getPdf(orderId, packingList) → { blob, name }: the Order and
  // Pack buttons then ask what to do with the PDF (see OrderViewHeader).
  getPdf,
  setSnack,
  AdditionalCostTotal,
  fulfilment = null,
  showHeader = true,
  // For users who can fulfil the order: the header's status select (see
  // OrderViewHeader); with `status.fulfil`, products get Picked ticks and a
  // Fulfil bar.
  status,
}) {
  const { t } = useTranslation("warehouse");
  // Phones and tablets open on the fulfilment section (status and charges,
  // as the old view shows them); desktops keep it folded under the panels.
  const [fulfilmentOpen, setFulfilmentOpen] = useState(
    () =>
      typeof window === "undefined" ||
      !window.matchMedia ||
      !window.matchMedia("(min-width: 1024px)").matches,
  );

  // Picked ticks kept on this device (the route sheet pick list's): for
  // Missing reports, and for every tick where Bolt has no picks.
  const [picks, setPicks] = useState(readPicks);
  // The parcel open in Tracking (a parcel id, "shipment" or null).
  const [trackedParcel, setTrackedParcel] = useState(null);
  const trackingRef = useRef(null);

  const statusName = data?.fullfilment_status?.name;
  const lines = useMemo(() => boltLines(data), [data]);
  const canPick =
    Boolean(status?.fulfil) &&
    !status?.disabled &&
    lines.length > 0 &&
    !pickingOver(statusName);

  // What is picked, from Bolt: the warehouse app's picks, the same the route
  // sheet's tick boxes show. A tick here picks the line there (what is left,
  // with no spot), so it shows on every phone and in the app, and the
  // server checks the role may pick. Where Bolt has no picks, or this login
  // can't see them, the ticks stay on this device as before.
  const pickOrders = useMemo(() => (canPick ? [data] : []), [canPick, data]);
  const boltPicks = useRoutePicks(pickOrders, canPick);
  const onBolt = (item) =>
    boltPicks.ready && Boolean(boltPicks.lineOf(item?.id));
  const shownPicks = useMemo(() => {
    if (!boltPicks.ready) return picks;
    const picked = new Set();
    lines.forEach((item) => {
      const id = String(item?.id);
      if (boltPicks.lineOf(id) ? boltPicks.isPicked(id) : picks.picked.has(id)) {
        picked.add(id);
      }
    });
    return { picked, missing: picks.missing };
  }, [boltPicks, lines, picks]);

  const onDevice = (update) =>
    setPicks((current) => {
      const next = update(current);
      writePicks(next);
      return next;
    });
  const toggle = (item) => {
    const id = String(item?.id);
    // Bolt's picks still loading: a tick now couldn't tell where it goes.
    if (boltPicks.status === "loading") return;
    if (!onBolt(item)) {
      onDevice((current) => togglePicked(current, item, statusName));
      return;
    }
    if (boltPicks.isBusy(id)) return;
    const shown = pickStateOf(shownPicks, item, statusName);
    if (shown === "picked" && !boltPicks.isPicked(id)) {
      // Shown picked because it was fulfilled before: unticking reports it
      // missing, as on this device.
      onDevice((current) => togglePick(current, [id], "missing"));
      return;
    }
    // Picking clears a Missing report.
    if (shown !== "picked" && picks.missing.has(id)) {
      onDevice((current) => togglePick(current, [id], "missing"));
    }
    boltPicks.toggle(data, item);
  };

  useEffect(() => {
    if (!boltPicks.message) return;
    setSnack?.({ open: true, text: boltPicks.message, severity: "error" });
    boltPicks.clearMessage();
  }, [boltPicks.message]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = canPick
    ? {
        stateOf: (item) => pickStateOf(shownPicks, item, statusName),
        isBusy: (item) =>
          boltPicks.status === "loading" || boltPicks.isBusy(item?.id),
        toggle,
      }
    : undefined;
  const plan = canPick ? fulfilPlan(shownPicks, lines, statusName) : null;

  // A parcel opened from Parcels: below lg, Tracking is further down the
  // page, so it comes up.
  const trackParcel = (id) => {
    setTrackedParcel(id);
    if (id == null || isDesktop()) return;
    setTimeout(() =>
      trackingRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      }),
    );
  };

  const hint = !plan
    ? ""
    : plan.done === plan.total
      ? "All picked. Fulfil, add photos and save."
      : plan.partial
        ? `${plan.total - plan.done} not picked: saves as partially fulfilled.`
        : "Tick each product as you pick it.";

  return (
    <div className="flex min-h-0 flex-col bg-ui-page">
      {showHeader && (
        <div className="relative z-20 shrink-0">
          <OrderViewHeader
            order={data}
            handleClose={handleClose}
            downloadPdf={downloadPdf}
            getPdf={getPdf}
            status={status}
          />
        </div>
      )}

      <div className="thin__scrollbar flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2 sm:p-3 lg:overflow-hidden">
        <div className="shrink-0">
          <OrderSummary data={data} AdditionalCostTotal={AdditionalCostTotal} />
        </div>

        {/* Below lg the panels are as tall as their content and the whole
            view scrolls; flex-1 here would squeeze them into whatever height
            the summary leaves (Products and Parcels showed only their
            headers on phones). From lg up they share the window and scroll
            inside. */}
        <div className="grid shrink-0 grid-cols-1 gap-2 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)]">
          <OrderItems data={data} pick={pick} onTrackParcel={trackParcel} />
          <OrderActivity
            data={data}
            setSnack={setSnack}
            trackedParcel={trackedParcel}
            onTrackParcel={trackParcel}
            trackingRef={trackingRef}
          />
        </div>

        {/* Warehouse app 0.8.0: what was scanned (store logins only). */}
        <OrderScans data={data} />

        {fulfilment && (
          <section className="shrink-0 overflow-hidden rounded-lg border border-ui-border bg-ui-surface">
            <button
              type="button"
              onClick={() => setFulfilmentOpen((open) => !open)}
              aria-expanded={fulfilmentOpen}
              className="flex min-h-[44px] w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-ui-row-hover lg:min-h-0 [@media(pointer:coarse)]:min-h-[44px]"
            >
              <span className="text-[13.5px] font-semibold text-ui-text">
                {t("orderView.fulfilmentAndCharges")}
              </span>
              <MdKeyboardArrowDown
                size={18}
                className={`shrink-0 text-ui-text-muted transition-transform ${
                  fulfilmentOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {fulfilmentOpen && (
              <div className="border-t border-ui-border p-3">{fulfilment}</div>
            )}
          </section>
        )}
      </div>

      {/* Picking: how far it is, and Fulfil — the status pop-up, as
          Fulfilled or partly, filled in from the ticks (photos are added
          there). Always in reach at the bottom. */}
      {plan && (
        <div className="shrink-0 border-t border-ui-border bg-ui-surface px-3 py-2 sm:px-4">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1 lg:flex lg:items-center lg:gap-3">
              <div className="flex shrink-0 flex-wrap items-baseline gap-x-2">
                <span className="text-[14px] font-semibold text-ui-text lg:text-[13px]">
                  Picked {plan.done} of {plan.total}
                </span>
                {plan.missing > 0 && (
                  <span className="text-[12px] font-medium text-state-danger-text">
                    {plan.missing} missing
                  </span>
                )}
              </div>
              <div
                role="progressbar"
                aria-label="Products picked"
                aria-valuemin={0}
                aria-valuemax={plan.total}
                aria-valuenow={plan.done}
                className="mt-1 h-1.5 overflow-hidden rounded-full bg-ui-surface-sunken lg:mt-0 lg:w-[220px] lg:shrink-0"
              >
                <div
                  className="h-full rounded-full bg-state-success-dot transition-[width]"
                  style={{ width: `${(plan.done / plan.total) * 100}%` }}
                />
              </div>
              <div className="mt-1 truncate text-[11.5px] text-ui-text-subtle lg:mt-0">
                {hint}
              </div>
            </div>
            <button
              type="button"
              onClick={() => status.fulfil(plan)}
              className="min-h-[44px] shrink-0 rounded-md bg-brand px-4 text-[14px] font-semibold text-brand-contrast transition-colors hover:bg-brand-hover lg:min-h-[36px] lg:text-[13px] [@media(pointer:coarse)]:min-h-[44px]"
            >
              {plan.partial ? "Partly fulfil" : "Fulfil order"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
